import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { NgZone } from '@angular/core';
import { Component } from '@angular/core';
import { Auth, GoogleAuthProvider, signInWithPopup } from '@angular/fire/auth';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { setPersistence, browserSessionPersistence } from '@firebase/auth';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [CommonModule, MatProgressSpinnerModule, MatSnackBarModule],
  standalone: true,
})
export class LoginComponent {
  isLoading = false;

  constructor(
    private auth: Auth,
    private router: Router,
    private route: ActivatedRoute,
    private httpClient: HttpClient,
    private snackBar: MatSnackBar,
    private ngZone: NgZone
  ) {
    this.auth.onAuthStateChanged(async (user) => {
      if (!user) {
        return;
      }
      let isUserAdmin = await this.isUserAdmin();

      // User is not admin - display a snackbar and return
      if (!isUserAdmin) {
        this.isLoading = false;

        this.snackBar.open('You are not authorized for this system', 'OK', {
          verticalPosition: 'top',
          duration: 5000,
        });

        await this.auth.signOut();
        return;
      }

      // signInWithPopup breaks NgZone for some reason
      this.ngZone.run(async () => {
        // User is admin - navigate to where they were headed, or the default
        await this.router.navigateByUrl(this.destination(), { replaceUrl: true });
      });
    });
  }

  /**
   * Where to land after signing in.
   *
   * The guard puts the attempted URL on the login route when a deep link is
   * followed unauthenticated. Only paths under /admin are honoured, so a
   * crafted link cannot use the login page to bounce someone somewhere else.
   */
  private destination(): string {
    const redirect = this.route.snapshot.paramMap.get('redirect');
    const allowed = redirect === '/admin' || redirect?.startsWith('/admin/');
    return allowed && redirect ? redirect : '/admin';
  }

  private async isUserAdmin(): Promise<boolean> {
    try {
      await lastValueFrom(this.httpClient.get(`${environment.baseURL}/me`));
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async signinWithGoogle() {
    this.isLoading = true;
    try {
      await setPersistence(this.auth, browserSessionPersistence);
      await signInWithPopup(this.auth, new GoogleAuthProvider());
    } catch (error) {
      console.log(error);
      this.isLoading = false;
    }
  }
}
